/**
 * products.js
 * Central product data store for AuraPrintiqo Shop.
 * Products are stored in localStorage so the admin panel can add/edit/delete
 * without needing a backend. Easy to migrate to an API later.
 */

const STORAGE_KEY = 'aura_products';

// ─── Seed data: shown when localStorage is empty ──────────────────────────────
export const SEED_PRODUCTS = [
  {
    id: 'prod-001',
    name: 'Sakura Pet Portrait Tee',
    collection: 'pet-anime',
    type: 'tshirt',
    occasion: ['birthday', 'gift'],
    price: 999,
    originalPrice: 1299,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'Navy', hex: '#1e3a5f' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/pet-cover.png', '/images/pet-after.png'],
    description: 'Your pet transformed into a stunning cherry blossom anime portrait. Perfect for pet lovers who want to celebrate their furry companions.',
    badge: 'Bestseller',
    inStock: true,
    customisable: true,
    tags: ['anime', 'pet', 'portrait', 'sakura'],
    createdAt: Date.now(),
  },
  {
    id: 'prod-002',
    name: 'Ghibli Couple Hoodie',
    collection: 'couple-anime',
    type: 'hoodie',
    occasion: ['anniversary', 'gift', 'couple'],
    price: 1799,
    originalPrice: 2199,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Grey', hex: '#9ca3af' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/couple-cover.png', '/images/couple-after.png'],
    description: 'Celebrate your love in beautiful Studio Ghibli-inspired style. Custom couple portraits printed on premium 320 GSM cotton hoodie.',
    badge: 'New',
    inStock: true,
    customisable: true,
    tags: ['ghibli', 'couple', 'hoodie', 'anime'],
    createdAt: Date.now(),
  },
  {
    id: 'prod-003',
    name: 'Corporate Team Tee',
    collection: 'corporate-custom',
    type: 'tshirt',
    occasion: ['corporate', 'team'],
    price: 799,
    originalPrice: null,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Black', hex: '#1a1a1a' },
      { name: 'Navy', hex: '#1e3a5f' },
      { name: 'Grey', hex: '#9ca3af' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/corporate-cover.png', '/images/corporate-after.png'],
    description: 'Professional custom branded T-shirts for your team. Bulk pricing available for 10+ orders. Tech themes, motivational quotes, or your own design.',
    badge: 'Bulk Available',
    inStock: true,
    customisable: true,
    tags: ['corporate', 'team', 'bulk', 'branded'],
    createdAt: Date.now(),
  },
  {
    id: 'prod-004',
    name: 'Anime Self Portrait Tee',
    collection: 'self-custom',
    type: 'tshirt',
    occasion: ['birthday', 'gift'],
    price: 1099,
    originalPrice: 1399,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Black', hex: '#1a1a1a' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/self-cover.jpg', '/images/self-after.png'],
    description: 'Transform yourself into a stunning anime character. Upload your photo and we handle the magic. Each piece is a limited edition of one.',
    badge: 'Limited',
    inStock: true,
    customisable: true,
    tags: ['anime', 'self', 'portrait', 'custom'],
    createdAt: Date.now(),
  },
  {
    id: 'prod-005',
    name: 'Pet Anime Hoodie',
    collection: 'pet-anime',
    type: 'hoodie',
    occasion: ['birthday', 'gift'],
    price: 1499,
    originalPrice: 1899,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Grey', hex: '#9ca3af' },
      { name: 'Black', hex: '#1a1a1a' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/pet-cover.png', '/images/pet-before.png'],
    description: 'Your beloved pet immortalised in anime art on a cozy 320 GSM hoodie. Makes the perfect gift for any pet parent.',
    badge: null,
    inStock: true,
    customisable: true,
    tags: ['anime', 'pet', 'hoodie'],
    createdAt: Date.now(),
  },
  {
    id: 'prod-006',
    name: 'Couple Anniversary Tee Set',
    collection: 'couple-anime',
    type: 'tshirt',
    occasion: ['anniversary', 'couple', 'gift'],
    price: 1599,
    originalPrice: 1999,
    colors: [
      { name: 'White', hex: '#FFFFFF' },
      { name: 'Black', hex: '#1a1a1a' },
    ],
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    images: ['/images/couple-after.png', '/images/couple-before.png'],
    description: 'Matching couple T-shirt set with your custom anime portrait. Perfect anniversary surprise for your partner.',
    badge: 'Set of 2',
    inStock: true,
    customisable: true,
    tags: ['couple', 'anniversary', 'matching', 'set'],
    createdAt: Date.now(),
  },
];

// ─── Storage helpers ───────────────────────────────────────────────────────────

export const getProducts = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
    // First run: seed with sample products
    localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_PRODUCTS));
    return SEED_PRODUCTS;
  } catch {
    return SEED_PRODUCTS;
  }
};

export const saveProducts = (products) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    return true;
  } catch {
    return false;
  }
};

export const addProduct = (product) => {
  const products = getProducts();
  const newProduct = {
    ...product,
    id: `prod-${Date.now()}`,
    createdAt: Date.now(),
  };
  saveProducts([...products, newProduct]);
  return newProduct;
};

export const updateProduct = (id, updates) => {
  const products = getProducts();
  const updated = products.map(p => p.id === id ? { ...p, ...updates } : p);
  saveProducts(updated);
  return updated;
};

export const deleteProduct = (id) => {
  const products = getProducts();
  saveProducts(products.filter(p => p.id !== id));
};

export const getProductById = (id) => {
  return getProducts().find(p => p.id === id) || null;
};

// ─── Filter options ────────────────────────────────────────────────────────────

export const COLLECTIONS_LIST = [
  { id: 'pet-anime',        label: 'Pet Anime',      emoji: '🐾' },
  { id: 'couple-anime',     label: 'Couple Anime',   emoji: '💑' },
  { id: 'corporate-custom', label: 'Corporate',      emoji: '🏢' },
  { id: 'self-custom',      label: 'Self Custom',    emoji: '✨' },
];

export const PRODUCT_TYPES = [
  { id: 'tshirt', label: 'T-Shirt' },
  { id: 'hoodie', label: 'Hoodie'  },
];

export const SIZES_LIST = ['S', 'M', 'L', 'XL', 'XXL'];

export const COLORS_LIST = [
  { name: 'White',  hex: '#FFFFFF' },
  { name: 'Black',  hex: '#1a1a1a' },
  { name: 'Navy',   hex: '#1e3a5f' },
  { name: 'Grey',   hex: '#9ca3af' },
  { name: 'Pink',   hex: '#f9a8d4' },
  { name: 'Yellow', hex: '#fde68a' },
];

export const OCCASIONS_LIST = [
  { id: 'birthday',    label: '🎂 Birthday'    },
  { id: 'anniversary', label: '💕 Anniversary' },
  { id: 'gift',        label: '🎁 Gift'        },
  { id: 'couple',      label: '👫 Couple'      },
  { id: 'corporate',   label: '🏢 Corporate'   },
  { id: 'team',        label: '👥 Team'        },
];
